import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';

import Schedule from 'features/scheduleBuilder/ScheduleBuilderView/resources/Schedule';

import { captureHydrationException, getSliceState } from '../helpers';
import { ScheduleFetchAction } from '../types';

import { SLICE_NAME } from './constants';
import {
  ClaimRequest,
  ClaimRequestRelationshipKeys,
  ClaimRequestSerializedEntity,
  ClaimRequestsState,
} from './types';

export const claimRequestsAdapter = createEntityAdapter<ClaimRequest>();

export const initialState: ClaimRequestsState = {
  ...claimRequestsAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    removeClaimRequest: (state, action) => {
      claimRequestsAdapter.removeOne(state, action.payload);
    },
    processClaimRequestApprovalResponse: state => {
      state.status = 'succeeded';
    },
  },
  extraReducers: builder => {
    builder.addCase(
      Schedule.resourceActionEvent('fetch'),
      (state: ClaimRequestsState, action: ScheduleFetchAction) => {
        state.status = 'succeeded';
        if (action.payload?.included) {
          try {
            const claimRequests = (
              action?.payload?.included as ClaimRequestSerializedEntity[]
            )
              .filter(
                (entity: ClaimRequestSerializedEntity) =>
                  entity.type === 'claimRequest'
              )
              .map((entity: ClaimRequestSerializedEntity) => ({
                id: entity.id,
                type: entity.type,
                status: entity.attributes.status,
                createdAt: entity.attributes.createdAt,
                shift: entity.relationships?.shift?.data,
                job: entity.relationships?.job?.data,
              }));
            claimRequestsAdapter.setAll(state, claimRequests);
          } catch (error) {
            captureHydrationException(state, error, SLICE_NAME);
          }
        }
      }
    );
  },
});

export const {
  selectById: selectClaimRequestById,
  selectIds: selectClaimRequestIds,
  selectEntities: selectClaimRequestEntities,
  selectAll: selectAllClaimRequests,
  selectTotal: selectTotalClaimRequests,
} = claimRequestsAdapter.getSelectors<RootState>(
  state => getSliceState(state)?.[SLICE_NAME] || initialState
);

export const { reducer, actions } = slice;
export const { removeClaimRequest, processClaimRequestApprovalResponse } =
  actions;

export const selectClaimRequestsStatus = (state: RootState) =>
  getSliceState(state)?.[SLICE_NAME]?.status || 'idle';

export const selectClaimRequestRelationshipId = ({
  state,
  id,
  relationship,
}: {
  state: RootState;
  id: EntityId;
  relationship: ClaimRequestRelationshipKeys;
}): EntityId | undefined => {
  const claimRequest = selectClaimRequestById(state, id);
  if (!claimRequest) return undefined;

  if (relationship === 'shift') {
    return claimRequest.shift?.id;
  }

  if (relationship === 'job') {
    return claimRequest.job?.id;
  }

  return undefined;
};

export const selectClaimRequestsByShiftId = (
  state: RootState,
  shiftId: EntityId
): ClaimRequest[] => {
  const allClaimRequests = selectAllClaimRequests(state);
  return allClaimRequests.filter(
    claimRequest => claimRequest.shift?.id === shiftId
  );
};
